"use client"

import { ArrowRight, Package } from 'lucide-react'
import { motion } from 'motion/react'
import { PRODUCT_CATEGORIES } from "@/lib/product-category-slug-map"

interface ShopByNeedTilesProps {
  selectedCategory: string
  onCategoryFilter: (category: string) => void
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.03, delayChildren: 0.05 },
  },
} as const

const tileVariants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' } },
} as const

export function ShopByNeedTiles({ selectedCategory, onCategoryFilter }: ShopByNeedTilesProps) {
  const isAllActive = selectedCategory === "All"

  return (
    <div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        role="group"
        aria-label="Filter products by category"
        aria-describedby="product-category-scroll-hint"
        className="scroll-hint-fade scroll-hint-fade-mobile-only -mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:pb-0"
      >
        <motion.button
          variants={tileVariants}
          onClick={() => onCategoryFilter("All")}
          className={`flex min-h-[44px] flex-shrink-0 snap-start items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-medium transition-colors duration-150 md:shrink ${
            isAllActive
              ? 'bg-linear-to-r from-green-600 to-emerald-600 text-white border-white/40 shadow-md'
              : 'bg-white/55 backdrop-blur-md backdrop-saturate-150 text-gray-700 border-white/60 hover:border-green-300 hover:bg-white/75'
          }`}
        >
          <Package className="w-3.5 h-3.5 flex-shrink-0" />
          All
        </motion.button>
        {PRODUCT_CATEGORIES.map(({ label, icon: Icon, category }) => {
          const isActive = selectedCategory === category
          return (
            <motion.button
              key={category}
              variants={tileVariants}
              onClick={() => onCategoryFilter(isActive ? "All" : category)}
              className={`flex min-h-[44px] flex-shrink-0 snap-start items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-medium transition-colors duration-150 md:shrink ${
                isActive
                  ? 'bg-linear-to-r from-green-600 to-emerald-600 text-white border-white/40 shadow-md'
                  : 'bg-white/55 backdrop-blur-md backdrop-saturate-150 text-gray-700 border-white/60 hover:border-green-300 hover:bg-white/75'
              }`}
            >
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              {label}
            </motion.button>
          )
        })}
      </motion.div>
      <p id="product-category-scroll-hint" className="mt-1 flex items-center gap-1 text-xs text-gray-500 md:hidden">
        Swipe to see all categories
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </p>
    </div>
  )
}
